import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";

interface CipherSelectorProps {
  onSelect?: (cipherId: string, cipherCode: string) => void;
  selectedCipherId?: string;
}

const SECURITY_BADGE: Record<string, string> = {
  basic: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  standard: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  advanced:
    "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
};

export default function CipherSelector({
  onSelect = () => {},
  selectedCipherId = "",
}: CipherSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["ciphers"],
    queryFn: () => apiClient.listCiphers({ limit: 100 }),
  });

  const ciphers = data?.ciphers ?? [];

  const filteredCiphers = ciphers.filter((cipher) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      cipher.name.toLowerCase().includes(searchLower) ||
      cipher.cipher_code.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="w-full bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-purple-100 dark:border-purple-800">
        <div className="flex items-center justify-center h-[200px] text-gray-500">
          <div className="space-y-2 text-center">
            <div className="animate-pulse">Loading ciphers...</div>
          </div>
        </div>
      </div>
    );
  }

  if (ciphers.length === 0) {
    return (
      <div className="w-full bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-purple-100 dark:border-purple-800">
        <div className="text-center py-8 space-y-4">
          <p className="text-gray-500">No ciphers available yet.</p>
          <p className="text-sm text-gray-400">
            Create your first cipher to start encrypting messages!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-purple-100 dark:border-purple-800">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-primary">Select Cipher</h2>
        <Badge variant="outline" className="text-xs">
          {ciphers.length} available
        </Badge>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="Search by name or code..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <ScrollArea className="h-[clamp(300px,50vh,600px)] pr-4">
        <RadioGroup
          value={selectedCipherId}
          onValueChange={(id) => {
            const cipher = ciphers.find((c) => c.id === id);
            if (cipher) onSelect(cipher.id, cipher.cipher_code);
          }}
          className="space-y-3"
        >
          {filteredCiphers.map((cipher) => {
            const isSelected = selectedCipherId === cipher.id;
            return (
              <div
                key={cipher.id}
                className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                  isSelected
                    ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400"
                    : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                }`}
                onClick={() => onSelect(cipher.id, cipher.cipher_code)}
              >
                <RadioGroupItem value={cipher.id} id={cipher.id} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <Label
                      htmlFor={cipher.id}
                      className="text-base font-medium cursor-pointer truncate"
                    >
                      {cipher.name}
                    </Label>
                    <Badge
                      className={`capitalize shrink-0 ${
                        SECURITY_BADGE[cipher.security_level] ?? ""
                      }`}
                    >
                      {cipher.security_level}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-gray-500 font-mono">
                      🕵️ {cipher.cipher_code}
                    </p>
                    {cipher.status === "draft" && (
                      <Badge variant="outline" className="text-xs">
                        Draft
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {filteredCiphers.length === 0 && ciphers.length > 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No ciphers found matching "{searchTerm}".</p>
              <p className="text-sm text-gray-400 mt-2">
                Try a different search term.
              </p>
            </div>
          )}
        </RadioGroup>
      </ScrollArea>
    </div>
  );
}
